import { keyBy } from 'lodash-es'
import { useMemo } from 'react'

import { useProduct } from '../application/product/ProductProvider'
import { ExcessLine, Quote } from '../domain'
import { ExcessCombination } from '../state/configuration'
import { useCoverLevelOption } from './coverOption'

interface Excesses {
  main: ExcessLine[]
  other: ExcessLine[]
  voluntary: Array<{
    voluntaryExcessCode: string
    excesses?: number[]
    excess: number
  }>
  combinations: ExcessCombination[] | undefined
}

export const isVoluntaryExcess =
  (voluntaryExcessCode: string) => (voluntary: Excesses['voluntary'][0]) =>
    voluntary.voluntaryExcessCode === voluntaryExcessCode

export const useExcesses = (quote: Quote | undefined): Excesses => {
  const { product } = useProduct()
  const { coverLevelConfig } = useCoverLevelOption(quote, product)

  return useMemo(() => {
    const excessesByCategory = keyBy<ExcessLine>(
      quote?.excessLines ?? [],
      'excessCategory',
    )

    const main = product.excess?.mainExcesses
      ?.map((it) => excessesByCategory[it])
      ?.filter(Boolean)
      ?.filter((it) => it.mandatoryExcess > 0)

    const other = product.excess?.otherExcesses
      ?.map((it) => excessesByCategory[it])
      ?.filter(Boolean)

    const voluntary = product.excess?.voluntaryExcesses?.map(
      ({ voluntaryExcessCode, excesses }) =>
        ({
          voluntaryExcessCode,
          excesses,
          excess: quote?.voluntaryExcesses[voluntaryExcessCode] ?? 0,
        } satisfies Excesses['voluntary'][0]),
    )

    return {
      main: main ?? [],
      other: other ?? [],
      voluntary: voluntary ?? [],
      combinations: coverLevelConfig?.excesses?.combinations,
    }
  }, [product, quote, coverLevelConfig])
}
